import { User } from 'models/entities/user';

import { Company, CompanyData } from './index';
import { ReadCollectionGql } from './gql';

type Data = {
  documents: CompanyData[];
  nextToken: string;
};

class CompanyCollection {

  readonly documents: Company[];
  readonly nextToken: string;

  constructor(data: Data = { documents: [], nextToken: '' }) {
    this.documents = data.documents.map(it => new Company(it));
    this.nextToken = data.nextToken;
  }

  async readAll(): Promise<this> {
    let collection = this;
    while (collection.nextToken) {
      const gql = await new ReadCollectionGql().fetch({ nextToken: collection.nextToken });
      if (!gql.result) throw new Error('invalid result');
      collection = collection.merge(new CompanyCollection(gql.result));
    }
    return collection;
  }

  merge(collection: CompanyCollection): this {
    const documents = Company.sort([...this.documents, ...collection.documents]);
    const nextToken = collection.nextToken;
    return Object.assign(Object.create(this.constructor.prototype), { ...this, documents, nextToken });
  }

  add(business: Company): this {
    const documents = Company.sort([...this.documents, business]);
    return Object.assign(Object.create(this.constructor.prototype), { ...this, documents });
  }

  replace(business: Company): this {
    const documents = Company.sort(this.documents.map(it => it.id === business.id ? business : it));
    return Object.assign(Object.create(this.constructor.prototype), { ...this, documents });
  }

  remove(business: Company): this {
    const documents = this.documents.filter(it => it.id !== business.id);
    return Object.assign(Object.create(this.constructor.prototype), { ...this, documents });
  }

  toJSON(): Data {
    const { nextToken } = this;
    const documents = this.documents.map(it => it.toJSON());
    return { documents, nextToken };
  }

  static async read(): Promise<CompanyCollection> {
    const gql = await new ReadCollectionGql().fetch();
    if (!gql.result) throw new Error('invalid result');
    return new CompanyCollection(gql.result);
  }

}

export { CompanyCollection };
export type { Data as CompanyCollectionData };